import React from "react";
import { Box, Divider, Stack } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ChevronRight } from "@mui/icons-material";

import { NAV } from "./config-layout";
import Scrollbar from "../component/scrollbar/scrollbar";
import { usePathname } from "../routes/hooks";
import { useRecoilValue } from "recoil";
import { authState } from "../recoil/authState";
import { useTheme } from "@emotion/react";
import { useNavData, useBottomNavData } from "./navData";
import { ListItemButton } from "@mui/material";

const NavItem = ({ item, fullPath, theme, isActive, hideParent, isChild }) => {
  // if (!item.child || item.child.length === 0) {
  if (item.isMenuItem) {
    // 최종 자식

    const ListITemBtnSx = {
      minHeight: 40,
      borderRadius: 0.75,
      textTransform: "capitalize",
      fontWeight: isActive ? "fontWeightSemiBold" : "fontWeightRegular",
      position: "relative",
      ...(isChild && {
        paddingLeft: 5.5,
      }),
      "&:hover": {
        background: "linear-gradient(90deg, #84C7C6 0%, #004B49 100%)",
        borderRadius: 0,
      },
      ...(isActive && {
        background: "linear-gradient(90deg, #84C7C6 0%, #004B49 100%)",
        borderLeft: "2px solid #FDFDFD",
        borderRadius: 0,
        "&:hover": {
          background: "linear-gradient(90deg, #84C7C6 0%, #004B49 100%)",
        },
      }),
    };

    if (item.isBtn) {
      return (
        <ListItemButton
          sx={ListITemBtnSx}
          onClick={() => {
            item.onClick();
          }}
        >
          <Box
            component="span"
            sx={{
              color: "theme.palette.common.white",
              fontSize: "16px",
            }}
          >
            {item.name}
          </Box>
        </ListItemButton>
      );
    }

    return (
      <>
        <Link to={item.path || fullPath} style={{ textDecoration: "none" }}>
          <ListItemButton sx={ListITemBtnSx}>
            <Box
              component="span"
              sx={{
                color: "white",
                fontSize: "16px",
                flex: 1,
              }}
            >
              {item.name}
            </Box>
            {isActive && (
              <Box
                sx={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "4px",
                  backgroundColor: "#FF6B35",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 1,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    color: "white",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  N
                </Box>
              </Box>
            )}
          </ListItemButton>
        </Link>
      </>
    );
  }

  if (hideParent) {
    return null;
  }

  if (item.isTopParent) {
    return null; // isTopParent는 RecursiveNav에서 처리
  }
  // 부모
};

const RecursiveNav = ({
  items,
  parentPath = "",
  pathname,
  theme,
  isMasterAuth,
  hideParent,
  expandedKeys = {},
  onToggleExpand,
  isChild = false,
  navigate,
}) => {
  const filteredItems = items.filter((item) => !item.requiresMasterAuth || isMasterAuth);
  
  // 첫 번째 자식의 경로를 찾는 헬퍼 함수
  const getFirstChildPath = (item, parentFullPath) => {
    if (!item.child || item.child.length === 0) {
      return null;
    }
    
    // 필터링된 자식 중 첫 번째 항목 찾기
    const firstChild = item.child.find((child) => !child.requiresMasterAuth || isMasterAuth);
    if (!firstChild) {
      return null;
    }
    
    // 자식에 path가 있으면 그것을 사용, 없으면 부모 경로 + 자식 key
    if (firstChild.path) {
      return firstChild.path.startsWith('/') ? firstChild.path : `/${firstChild.path}`;
    }
    
    const childPath = `${parentFullPath}/${firstChild.key}`;
    return childPath.startsWith('/') ? childPath : `/${childPath}`;
  };
  
  return (
    <Stack spacing={0.5}>
      {filteredItems
        .map((item, index) => {
          const fullPath = item.path || `${parentPath}/${item.key}`; // Construct full path or use custom path
          // fullPath를 /로 시작하도록 정규화
          const normalizedFullPath = fullPath.startsWith('/') ? fullPath : `/${fullPath}`;
          const isActive = pathname.startsWith(normalizedFullPath);
          const hasChildren = item.child?.length > 0;
          const isExpanded = item.isTopParent 
            ? (expandedKeys[item.key] !== undefined ? expandedKeys[item.key] : false)
            : true;
          const isLastItem = index === filteredItems.length - 1;
          const isChildItem = isChild || hideParent;

          return (
            <React.Fragment key={item.key}>
              {item.isTopParent ? (
                <>
                  <ListItemButton
                    onClick={() => {
                      if (hasChildren && onToggleExpand) {
                        onToggleExpand(item.key);
                      }
                      // 첫 번째 자식 경로로 이동
                      if (hasChildren && navigate) {
                        const firstChildPath = getFirstChildPath(item, normalizedFullPath);
                        if (firstChildPath) {
                          navigate(firstChildPath);
                        }
                      }
                    }}
                    sx={{
                      minHeight: 40,
                      borderRadius: 0.75,
                      fontWeight: "fontWeightSemiBold",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      paddingX: 2,
                      "&:hover": {
                        background: "linear-gradient(90deg, #84C7C6 0%, #004B49 100%)",
                        borderRadius: 0,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      {item.icon && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            color: alpha(theme.palette.common.white, 1),
                            fontSize: "16px",
                            "& svg": {
                              fontSize: "16px",
                            },
                          }}
                        >
                          {item.icon}
                        </Box>
                      )}
                      <Box
                        component="span"
                        sx={{
                          color: alpha(theme.palette.common.white, 1),
                          fontSize: "16px",
                          fontWeight: "fontWeightSemiBold",
                        }}
                      >
                        {item.name}
                      </Box>
                    </Box>
                    {hasChildren && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          ml: 1,
                          transition: "transform 0.3s ease-in-out",
                          transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                        }}
                      >
                        <ChevronRight
                          sx={{
                            color: alpha(theme.palette.common.white, 0.8),
                            fontSize: "20px",
                          }}
                        />
                      </Box>
                    )}
                  </ListItemButton>
                  {hasChildren && (
                    <Box
                      sx={{
                        maxHeight: isExpanded ? "1000px" : 0,
                        overflow: "hidden",
                        transition: "max-height 0.7s ease-in-out",
                      }}
                    >
                      {isExpanded && (
                        <>
                          <RecursiveNav
                            hideParent={hideParent}
                            items={item.child}
                            parentPath={normalizedFullPath}
                            pathname={pathname}
                            theme={theme}
                            isMasterAuth={isMasterAuth}
                            expandedKeys={expandedKeys}
                            onToggleExpand={onToggleExpand}
                            isChild={true}
                            navigate={navigate}
                          />
                          {!isLastItem && (
                            <Divider
                              sx={{
                                borderBottomWidth: "1.4px",
                                backgroundColor: alpha(theme.palette.common.white, 0.3),
                                mt: 0.5,
                                mx: 2,
                              }}
                            />
                          )}
                        </>
                      )}
                    </Box>
                  )}
                </>
              ) : (
                <>
                  <Box
                    className={isActive && isChildItem && item.isMenuItem ? "active-child-gradient" : ""}
                    sx={{}}
                  >
                    <NavItem
                      isParent={item.child?.length > 0}
                      item={item}
                      fullPath={normalizedFullPath}
                      theme={theme}
                      isActive={isActive}
                      hideParent={hideParent}
                      isChild={hideParent ? false : true}
                    />
                  </Box>
                  {hasChildren && (
                    <Box
                      sx={{
                        maxHeight: isExpanded ? "1000px" : 0,
                        overflow: "hidden",
                        transition: "max-height 0.7s ease-in-out",
                        ml: 2,
                        mr: 2,
                      }}
                    >
                      {isExpanded && (
                        <RecursiveNav
                          hideParent={hideParent}
                          items={item.child}
                          parentPath={normalizedFullPath}
                          pathname={pathname}
                          theme={theme}
                          isMasterAuth={isMasterAuth}
                          expandedKeys={expandedKeys}
                          onToggleExpand={onToggleExpand}
                          isChild={hideParent ? false : true}
                          navigate={navigate}
                        />
                      )}
                    </Box>
                  )}
                </>
              )}
            </React.Fragment>
          );
        })}
    </Stack>
  );
};

export default function Nav() {
  const pathname = usePathname();
  const navigate = useNavigate();
  const auth = useRecoilValue(authState);
  const isMasterAuth = auth.user.club_admin_level === 10;
  const theme = useTheme();
  const navData = useNavData(); // 동적으로 navData 가져오기
  const bottomNavData = useBottomNavData();
  const [expandedKeys, setExpandedKeys] = useState({ yb: true });

  const handleToggleExpand = (key) => {
    setExpandedKeys((prev) => {
      const currentState = prev[key] !== undefined ? prev[key] : false;
      
      // 아코디언 방식: 하나를 펼치면 다른 모든 항목은 닫힘
      if (!currentState) {
        // 현재 항목이 닫혀있으면, 모든 항목을 닫고 현재 항목만 열기
        return {
          [key]: true,
        };
      } else {
        // 현재 항목이 열려있으면, 현재 항목만 닫기
        return {
          ...prev,
          [key]: false,
        };
      }
    });
  };

  return (
    <Box
      sx={{
        // flexShrink: { lg: 0 },
        // width: { lg: NAV.WIDTH },
        width: NAV.WIDTH,
        "*": { color: "#FAF9F6" },
      }}
    >
      <Box
        width={280}
        height="100%"
        position="fixed"
        sx={{
          position: "fixed",
          backgroundColor: "#004B49",
        }}
      >
        <Scrollbar
          sx={{
            height: 1,
            "& .simplebar-content": {
              height: "100%",
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#004B49",
            },
          }}
        >
          {/* Main Navigation Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              minHeight: "100px",
              px: 2,
              marginBottom: "32px",
            }}
          >
            <Link
              to="/yb/yearBookingList"
              style={{
                textDecoration: "none",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  width: `70px`,
                  height: `70px`,
                  flexShrink: 0,
                  backgroundPosition: "center",
                  backgroundSize: "cover",
                  backgroundRepeat: "no-repeat",
                  backgroundImage: "url(/teeshot_logo.png)",
                }}
              />
              <Stack sx={{ flexDirection: "column", gap: 0.3 }}>
                <Box sx={{ fontSize: "20px", lineHeight: 1.2, color: "white" }}>
                  TeeShot
                </Box>
                <Box sx={{ fontSize: "13px", lineHeight: 1.2, color: "white" }}>
                  골프친구 티샷
                </Box>
              </Stack>
              {/* <img src="/teeshot-logo.svg" /> */}

              {/* HBMP 부킹 관리자 */}
            </Link>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              height: "100%",
            }}
          >
            <Stack component="nav" spacing={0.5} sx={{ px: 2, flex: 1 }}>
              <RecursiveNav
                items={navData}
                pathname={pathname}
                theme={theme}
                isMasterAuth={isMasterAuth}
                expandedKeys={expandedKeys}
                onToggleExpand={handleToggleExpand}
                navigate={navigate}
              />
            </Stack>

          {/* Bottom Navigation Section */}
          <Stack 
            component="nav" 
            spacing={0.5}
            sx={{ 
              px: 2, 
              pb: 2,
              bgcolor: "#004B49",
              "& .MuiBox-root": {
                marginLeft: 0,
              },
            }}
          >
            <RecursiveNav
              hideParent={true}
              items={bottomNavData}
              pathname={pathname}
              theme={theme}
              isMasterAuth={isMasterAuth}
              expandedKeys={expandedKeys}
              onToggleExpand={handleToggleExpand}
              isChild={false}
              navigate={navigate}
            />
          </Stack>
          </Box>
        </Scrollbar>
      </Box>
    </Box>
  );
}
